import React, { useRef, useEffect } from 'react';
import { Player } from '../game/Player';
import { getDepthScale, DEFAULT_GROUND_Y, WALKABLE_DEPTH, GROUND_HEIGHT, getDrawY } from '../game/constants';

const NFTCharacterPreview = ({ nftId, color, size = 120 }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const frameRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    // Create a temporary player for drawing
    const player = new Player(0, 0, color || '#FF3B30', null, nftId);
    player.direction = 1;
    player.width = 50;
    player.height = 75;
    // Set groundY for ~1.0 scale in preview (neutral perspective)
    player.groundY = WALKABLE_DEPTH * 0.68;

    const animate = () => {
      frameRef.current += 0.04;
      player.animFrame = frameRef.current % 4;

      ctx.clearRect(0, 0, w, h);

      // Dark gradient background
      const bg = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w * 0.7);
      bg.addColorStop(0, `${color || '#9945FF'}18`);
      bg.addColorStop(1, 'transparent');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Position character centered
      const charX = (w - player.width) / 2;
      const charY = (h - player.height) / 2 - 5;
      player.y = charY;

      // Subtle floating bob
      player.y += Math.sin(frameRef.current * 2) * 3;

      ctx.save();
      // Draw the character using its native draw method
      // We offset by charX as cameraX to center it
      player.x = charX;
      player.draw(ctx, 0);
      ctx.restore();

      // Ground shadow
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.ellipse(w / 2, h - 12, 20, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
      }
    };
  }, [nftId, color]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className="w-full h-full"
      style={{ imageRendering: 'pixelated' }}
      data-testid={`nft-preview-${nftId}`}
    />
  );
};

export default NFTCharacterPreview;
